import {
  Avatar,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Spin,
  Switch,
  Typography,
  message,
} from "antd";
import { Venue } from "interfaces/venue";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as LocationIcon } from "assets/images/location-icon.svg";
import { ReactComponent as EditIcon } from "assets/images/edit-icon.svg";
import {
  InstagramOutlined,
  TikTokOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { deleteVenue, updateVenue } from "services/SauceService";
import { CollabAvailability } from "pages/collab/CollabDetail";
import {
  BusinessState,
  getBusinessInfo,
  getBusinessSubscription,
} from "redux-context/business";
import { ReactComponent as CloseIcon } from "assets/images/trash-icon.svg";
import { isMobile } from "react-device-detect";
import ModalVenue from "pages/collab/ModalVenue";
import LocationForm from "./LocationForm";

const Locations: React.FC = () => {
  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business
  );

  const dispatch = useDispatch<any>();
  const [selectedVenue, setSelectedVenue] = useState<Venue>();
  const [showModalVenue, setshowModalVenue] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const [venues, setVenues] = useState<Venue[]>([]);

  const canAddVenue =
    subscription?.locationsAllowed > subscription?.locationsUsage;
  const [searchTerm, setSearchTerm] = useState<string>("");

  useMemo(() => {
    if (business?.venues) {
      setVenues(
        [...business.venues].sort((a, b) => {
          const aDisabled = a.disabled === undefined ? false : a.disabled;
          const bDisabled = b.disabled === undefined ? false : b.disabled;
          return Number(aDisabled) - Number(bDisabled);
        })
      );
    }
  }, [business?.venues]);

  const filteredVenues = useMemo(() => {
    return venues.filter(
      (location: Venue) =>
        location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        location.addressDisplay.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [venues, searchTerm]);

  const onClickSelectedVenue = (venue: Venue) => {
    setSelectedVenue(venue);
    setEditing(true);
  };

  const onEditClick = () => {
    setEditing(true);
  };

  const onCancelEditClick = (saved?: boolean) => {
    setEditing(false);
    setSelectedVenue(undefined);
    setEditing(false);
  };

  const onCancelClick = () => {
    setSelectedVenue(undefined);
    setEditing(false);
  };

  const onSaveVenue = async () => {
    dispatch(getBusinessInfo());
    dispatch(getBusinessSubscription());
    setshowModalVenue(false);
  };

  const onClickRemoveVenue = (location?: Venue) => {
    Modal.confirm({
      content: "Are you sure you want to remove this location?",
      icon: "",
      onOk: async () => {
        setLoading(true);
        try {
          const response: any = await deleteVenue({
            businessId: business._id,
            venueId: location?._id,
          });
          if (response.message) {
            dispatch(getBusinessInfo());
            dispatch(getBusinessSubscription());
            setEditing(false);

            setSelectedVenue(undefined);
            message.success("Location removed successfully");
          }
        } catch (error) {
          console.error(error);
          message.error("Failed to remove location");
        }
        setLoading(false);
      },
      okButtonProps: {
        type: "primary",
      },
    });
  };

  const onChangeLocationStatus = async (location: Venue) => {
    const businessId = business._id;
    const params = {
      ...location,
      businessId,
      venueId: location._id,
      disabled: !location.disabled,
    };
    setLoading(true);
    try {
      const response: any = await updateVenue(params);
      if (response.message) {
        setVenues((prevVenues) =>
          prevVenues.map((v) =>
            v._id === location._id ? { ...v, disabled: !location.disabled } : v
          )
        );
        message.success("Location status updated successfully");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to update location status");
    }
    setLoading(false);
  };

  if (editing)
    return (
      <LocationForm
        selectedVenue={selectedVenue}
        onCloseForm={onCancelEditClick}
      />
    );

  if (selectedVenue)
    return (
      <Card className="locations">
        <Row justify="center" gutter={[20, 20]}>
          <Col xs={8}>
            <Typography.Text>Name</Typography.Text>
          </Col>
          <Col xs={16}>{selectedVenue.name}</Col>
          <Col xs={8}>
            <Typography.Text>Address</Typography.Text>
          </Col>
          <Col xs={16}>{selectedVenue.addressDisplay}</Col>
          <Col xs={8}>
            <Typography.Text>Point of contact</Typography.Text>
          </Col>
          <Col xs={16}>
            {selectedVenue?.pocs?.map((pointOfContact) => (
              <div
                key={`POINT_OF_CONTACT_${pointOfContact?._id}_${pointOfContact?.email}`}
                className="poc-description"
              >
                <span className="name">{pointOfContact?.name}</span> {" ∘ "}
                <span>{pointOfContact?.phone}</span>
                {" ∘ "}
                <span>{pointOfContact?.email}</span>
              </div>
            ))}
          </Col>
          <Col xs={8}>
            <Typography.Text>Links to social media</Typography.Text>
          </Col>
          <Col xs={16}>
            <div className="link-social">
              <InstagramOutlined />{" "}
              {selectedVenue.instagramUrl?.includes("http") ? (
                <a
                  href={selectedVenue.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {selectedVenue.instagramUrl}
                </a>
              ) : (
                selectedVenue.instagramUrl
              )}
            </div>
            <div className="link-social">
              <TikTokOutlined />{" "}
              {selectedVenue.tiktokUrl?.includes("http") ? (
                <a
                  href={selectedVenue.tiktokUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {selectedVenue.tiktokUrl}
                </a>
              ) : (
                selectedVenue.tiktokUrl
              )}
            </div>
          </Col>
          <Col xs={8}>
            <Typography.Text>Business hours</Typography.Text>
          </Col>
          <Col xs={16}>
            <Row gutter={[14, 14]}>
              {selectedVenue?.venueWorkingHours?.map((availability, index) => (
                <CollabAvailability
                  availability={availability}
                  key={`COLLAB_AVAILABILITY${index}`}
                />
              ))}
            </Row>
          </Col>
        </Row>
        <Row justify="end" style={{ gap: 8 }}>
          <Button icon={<EditIcon />} onClick={onEditClick}>
            Edit
          </Button>

          <Button
            icon={<CloseIcon />}
            onClick={() => onClickRemoveVenue(selectedVenue)}
            style={{ color: "#FF354A" }}
          />
          <Button htmlType="button" type="text" onClick={onCancelClick}>
            Cancel
          </Button>
        </Row>
      </Card>
    );

  return (
    <Spin spinning={loading} size="large" style={{ width: "100%" }}>
      <Row gutter={[12, 12]} className="locations">
        <ModalVenue
          open={showModalVenue}
          onCancel={() => setshowModalVenue(false)}
          onSave={onSaveVenue}
        />
        <Col xs={24}>
          <Input
            placeholder="Search locations"
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: 16, width: "30%" }}
          />
        </Col>
        <Col xs={24}>
          <div className="location-disclaimer">
            <p>
              In this section you can manage the locations. Your plan allows you
              to create collabs for up to {subscription?.locationsAllowed}{" "}
              locations simultaneously.
            </p>

            <Button
              type="primary"
              disabled={!canAddVenue}
              onClick={() => setshowModalVenue(true)}
            >
              Add Location
            </Button>
          </div>
        </Col>

        {filteredVenues.map((location: Venue) => (
          <Col xs={24} key={location.uuid}>
            <Card>
              <Row>
                <Col xs={24} md={14}>
                  <div className="content">
                    <Avatar
                      src={<LocationIcon style={{ width: 20, height: 20 }} />}
                      size="large"
                      style={{ backgroundColor: "#ffac39", minWidth: 40 }}
                    />
                    <div>
                      <span
                        className="label-strong"
                        style={{ fontSize: isMobile ? 16 : "unset" }}
                      >
                        {location.name}
                      </span>
                      <span
                        className="text-regular-small"
                        style={{ display: "block" }}
                      >
                        {location.addressDisplay}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col xs={24} md={10}>
                  <div className="pocs">
                    <div>
                      {location.pocs?.length > 0 ? (
                        <div>
                          <span className="name">{location.pocs[0].name}</span>
                          {location.pocs?.length > 1 && (
                            <span className="other">
                              + {location.pocs.length - 1} POC
                            </span>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="name">&nbsp;</span>
                        </div>
                      )}
                    </div>
                    <Button
                      icon={<EditIcon />}
                      onClick={() => onClickSelectedVenue(location)}
                    />
                    <Switch
                      checked={!location.disabled}
                      style={{ marginLeft: 8 }}
                      onChange={() => onChangeLocationStatus(location)}
                    />
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
      </Row>
    </Spin>
  );
};

export default Locations;
